// eslint-disable-next-line no-restricted-imports
// oxlint-disable-next-line no-restricted-imports
import { fromJS } from '@gonfalon/immutable-shim/immutableShim';
import type { ImmutableServerError } from '@gonfalon/legacy-http-client/httpUtils';
import type { ImmutableMap } from '@gonfalon/legacy-immutable/immutableUtils';

import type { ApprovalAction } from '#actions/approvals';
import type { FlagAction } from '#actions/flags';
import type { ScheduledChangesAction } from '#actions/scheduledChanges';
import { useSelector } from '#hooks/useSelector';
import type { GlobalState } from '#reducers/state';
import type { Flag } from '#utils/flags/records/Flag';

import registry from './registry';

type FlagManagerState = ImmutableMap<{
  original: Flag | null;
  modified: Flag | null;
  isSaving: boolean;
  error: ImmutableServerError | null;
  isFlagUpdateComplete: boolean;
}>;

export const initialState: FlagManagerState = fromJS({
  original: null,
  modified: null,
  isSaving: false,
  error: null,
  isFlagUpdateComplete: false,
});

export function flagManager(
  state: FlagManagerState = initialState,
  action: FlagAction | ScheduledChangesAction | ApprovalAction,
) {
  function isSameFlag(flag: Flag) {
    const originalFlag = state.get('original');
    if (originalFlag) {
      return originalFlag.key === flag.key;
    }
    return false;
  }

  switch (action.type) {
    case 'flags/INITIALIZE_FLAG_CONFIG_MANAGER':
      return initialState.merge({
        original: action.flag,
        modified: action.flag,
      });
    case 'flags/CHANGE_FLAG_CONFIGURATION':
      return state.merge({
        modified: action.flag,
        error: null,
        isSaving: false,
      });
    case 'approvals/CREATE_APPROVAL_REQUEST':
      return state.merge({ isSaving: true, error: null });
    case 'scheduledChanges/CREATE_FLAG_SCHEDULED_CHANGE_DONE':
    case 'approvals/CREATE_APPROVAL_REQUEST_DONE':
    case 'flags/RESET_FLAG_CONFIGURATION':
      return initialState.merge({
        original: state.get('original'),
        modified: state.get('original'),
        isSaving: false,
      });
    case 'approvals/CREATE_APPROVAL_REQUEST_FAILED':
      return state.merge({
        error: action.error,
        isSaving: false,
      });
    case 'flags/UPDATE_FLAG':
      if (!isSameFlag(action.newFlag)) {
        return state;
      }
      return state.merge({
        isSaving: true,
        error: null,
      });
    case 'flags/UPDATE_FLAG_FAILED':
      if (!isSameFlag(action.flag)) {
        return state;
      }
      return state.merge({
        error: action.error,
        isSaving: false,
      });
    case 'flags/UPDATE_FLAG_DONE':
    case 'flags/UPDATE_RULE_EXCLUSION_DONE':
    case 'flags/UPDATE_FLAG_GOALS_DONE':
      if (!isSameFlag(action.flag)) {
        return state;
      }
      return initialState.merge({
        original: action.flag,
        modified: action.flag,
        isFlagUpdateComplete: true,
      });
    case 'flags/DISCARD_SPECIFIC_CONFIG_CHANGES':
      if (!isSameFlag(action.flag)) {
        return state;
      }

      return state.merge({
        modified: action.flag,
      });
    default:
      return state;
  }
}
registry.addReducers({ flagManager });

export const flagManagerSelector = (state: GlobalState) => state.flagManager;

function useFlagManager() {
  return useSelector(flagManagerSelector);
}

export function useFlagManagerOriginalState() {
  return useFlagManager().get('original');
}
declare module '#reducers/state' {
  interface GlobalState {
    flagManager: ReturnType<typeof flagManager>;
  }
}
